import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Shield, Key, Smartphone, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

export default function Settings() {
  const { user } = useAuth();
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleEnableMFA = () => {
    setShowMfaSetup(true);
    toast.info('MFA setup initiated. Scan the QR code with your authenticator app.');
  };

  const handleVerifyMFA = (e) => {
    e.preventDefault();
    // In production, this would verify with backend
    if (mfaCode.length === 6) {
      setMfaEnabled(true);
      setShowMfaSetup(false);
      toast.success('MFA enabled successfully!');
      setMfaCode('');
    } else {
      toast.error('Please enter a valid 6-digit code');
    }
  };

  const handleDisableMFA = () => {
    setMfaEnabled(false);
    toast.success('MFA disabled');
  };

  const handleChangePassword = (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 12) {
      toast.error('Password must be at least 12 characters long');
      return;
    }

    // Password strength check
    const hasUpperCase = /[A-Z]/.test(passwordData.newPassword);
    const hasLowerCase = /[a-z]/.test(passwordData.newPassword);
    const hasNumbers = /\d/.test(passwordData.newPassword);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(passwordData.newPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
      toast.error('Password must include uppercase, lowercase, numbers, and special characters');
      return;
    }

    toast.success('Password changed successfully');
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Settings</h1>
        <p className="text-gray-600">Manage your account security and preferences</p>
      </div>

      <Tabs defaultValue="security" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="security" data-testid="security-tab">
            <Shield className="w-4 h-4 mr-2" />
            Security & MFA
          </TabsTrigger>
          <TabsTrigger value="api">
            <Key className="w-4 h-4 mr-2" />
            API Keys
          </TabsTrigger>
        </TabsList>

        <TabsContent value="security" className="space-y-6">
          {/* MFA Setup Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Smartphone className="w-5 h-5 mr-2" />
                Multi-Factor Authentication (MFA)
              </CardTitle>
              <CardDescription>
                Add an extra layer of security to your account with time-based one-time passwords (TOTP)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h3 className="font-medium">MFA Status</h3>
                  <p className="text-sm text-gray-500">
                    {mfaEnabled ? 'Two-factor authentication is enabled' : 'Protect your account with MFA'}
                  </p>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                  mfaEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                }`}>
                  {mfaEnabled ? 'Enabled' : 'Disabled'}
                </div>
              </div>

              {!mfaEnabled && !showMfaSetup && (
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-2">How MFA Works:</h4>
                    <ol className="text-sm text-blue-800 list-decimal list-inside space-y-1">
                      <li>Install an authenticator app (Google Authenticator, Authy, Microsoft Authenticator)</li>
                      <li>Scan the QR code with your authenticator app</li>
                      <li>Enter the 6-digit code to verify setup</li>
                      <li>You'll need this code every time you log in</li>
                    </ol>
                  </div>
                  <Button 
                    onClick={handleEnableMFA} 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="enable-mfa-button"
                  >
                    <Smartphone className="w-4 h-4 mr-2" />
                    Enable Multi-Factor Authentication
                  </Button>
                </div>
              )}

              {showMfaSetup && (
                <div className="space-y-4 p-4 border rounded-lg">
                  <h4 className="font-semibold">Setup MFA</h4>
                  <div className="flex flex-col items-center space-y-4">
                    <div className="w-48 h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                      <div className="text-center">
                        <p className="text-sm text-gray-600 mb-2">QR Code</p>
                        <p className="text-xs text-gray-500 px-4">
                          Scan this with your authenticator app
                        </p>
                        <div className="mt-4 p-4 bg-white rounded border-2 border-dashed border-gray-300">
                          <p className="text-xs font-mono">DEMO: QR Code would appear here</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="w-full">
                      <p className="text-sm text-gray-600 mb-2">Manual entry key:</p>
                      <code className="block p-2 bg-gray-100 rounded text-sm font-mono">
                        JBSWY3DPEHPK3PXP
                      </code>
                    </div>

                    <form onSubmit={handleVerifyMFA} className="w-full space-y-4">
                      <div>
                        <Label htmlFor="mfaCode">Enter 6-digit code from your app</Label>
                        <Input
                          id="mfaCode"
                          placeholder="000000"
                          value={mfaCode}
                          onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          maxLength={6}
                          className="text-center text-2xl tracking-widest"
                          data-testid="mfa-code-input"
                        />
                      </div>
                      <div className="flex space-x-2">
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => { setShowMfaSetup(false); setMfaCode(''); }}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                        <Button 
                          type="submit" 
                          className="flex-1 bg-blue-600 hover:bg-blue-700"
                          data-testid="verify-mfa-button"
                        >
                          Verify & Enable
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {mfaEnabled && (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800">
                      ✓ Your account is protected with multi-factor authentication
                    </p>
                  </div>
                  <Button 
                    onClick={handleDisableMFA} 
                    variant="outline"
                    className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="disable-mfa-button"
                  >
                    Disable MFA
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Password Change Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Lock className="w-5 h-5 mr-2" />
                Change Password
              </CardTitle>
              <CardDescription>
                Update your password to keep your account secure
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                    required
                    data-testid="current-password-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                    required
                    data-testid="new-password-input"
                  />
                  <div className="text-xs text-gray-500 space-y-1">
                    <p>Password must:</p>
                    <ul className="list-disc list-inside ml-2">
                      <li>Be at least 12 characters long</li>
                      <li>Include uppercase and lowercase letters</li>
                      <li>Include numbers</li>
                      <li>Include special characters (!@#$%^&*)</li>
                    </ul>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                    required
                    data-testid="confirm-password-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  data-testid="change-password-button"
                >
                  Change Password
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Additional Security Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Additional Security Settings</CardTitle>
              <CardDescription>Configure other security options</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h3 className="font-medium">Session Timeout</h3>
                  <p className="text-sm text-gray-500">Automatic logout after inactivity</p>
                </div>
                <div className="flex items-center space-x-2">
                  <Input type="number" placeholder="30" className="w-20" defaultValue="30" /> 
                  <span className="text-sm text-gray-500">minutes</span>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h3 className="font-medium">Audit Logging</h3>
                  <p className="text-sm text-gray-500">Track all user actions (GMP compliance)</p>
                </div>
                <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                  Enabled
                </div>
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h3 className="font-medium">Login Notifications</h3>
                  <p className="text-sm text-gray-500">Get notified of new login attempts</p>
                </div>
                <Button variant="outline" size="sm">Enable</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="api" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>API Keys</CardTitle>
              <CardDescription>Manage API keys for external integrations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h3 className="font-medium">Primary API Key</h3>
                    <p className="text-sm text-gray-500 font-mono">sk_live_••••••••••••••••</p>
                  </div>
                  <Button variant="outline">Regenerate</Button>
                </div>

                <Button variant="outline" className="w-full">
                  <Key className="w-4 h-4 mr-2" />
                  Generate New API Key
                </Button>

                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>⚠️ Important:</strong> Keep your API keys secure. Never share them or commit them to version control.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
